export const AUTO_PROCESS_CONCURRENCY = 8;
/** Text-only Auto-process gate. Do not wait a full vision minute per card. */
export const AUTO_PROCESS_GATE_TIMEOUT_MS = 20000;
export const REVIEWS_PAGE_SIZE = 100;
/** Store brand catalogs (CK 378, TH 275) must fit; inject-gate caps at 500 names. */
export const CATALOG_FETCH_SIZE = 500;

export type AutoProcessOutcome = 'injected' | 'rejected' | 'held' | 'skip';

export type AutoProcessResult = {
  outcome: AutoProcessOutcome;
  reason?: string;
};

export function parseKeywordsFromTaskText(keywordsText?: string | null): string[] | undefined {
  if (!keywordsText || !keywordsText.trim()) return undefined;
  const keywords = [
    ...new Set(
      keywordsText
        .split(/[,\n]/)
        .map(s => s.trim())
        .filter(Boolean)
    ),
  ];
  return keywords.length ? keywords : undefined;
}

export function catalogNamesFrom(products: Array<{ name?: string } | null | undefined>): string[] {
  return products.map(p => String(p?.name || '').trim()).filter(Boolean);
}

export function listingBrandFromGroup(opts: {
  product: any;
  taskBrand?: string;
  parentBrand?: string;
  pageBrand?: string;
}): string {
  return (
    String(opts.product?.brand || '').trim() ||
    String(opts.taskBrand || '').trim() ||
    String(opts.parentBrand || '').trim() ||
    String(opts.pageBrand || '').trim()
  );
}

export function missingAutoInjectFields(opts: {
  product: any;
  brand: string;
  productName: string;
  requireStoreProduct?: boolean;
}): string[] {
  const missing: string[] = [];
  if (!String(opts.product?.url || '').trim()) missing.push('URL');
  if (!String(opts.product?.title || '').trim()) missing.push('Title');
  const seller = opts.product?.seller || opts.product?.seller_name;
  if (!seller || !String(seller).trim()) missing.push('Seller');
  if (!String(opts.brand || '').trim()) missing.push('Brand');
  if (opts.requireStoreProduct !== false && !String(opts.productName || '').trim()) {
    missing.push('Store product');
  }
  return missing;
}

export function createBrandCatalogCache(
  fetchCatalog: (brand: string) => Promise<any[]>
): (brand: string) => Promise<any[]> {
  const cache = new Map<string, Promise<any[]>>();
  return (brand: string) => {
    const key = brand.trim();
    if (!key) return Promise.resolve([]);
    let pending = cache.get(key);
    if (!pending) {
      pending = fetchCatalog(key).catch(err => {
        console.error('Auto-process catalog fetch failed:', err);
        return [];
      });
      cache.set(key, pending);
    }
    return pending;
  };
}

/**
 * Held cards still worth a bulk reject.
 * Cards still on the loaded page are included. Cards missing from a fully loaded
 * feed were already cleared by hand. Cards missing from a partial page may sit
 * further down the queue and are included.
 */
export function heldGroupsToReject<T extends { groupKey: string }>(opts: {
  held: T[];
  loadedGroupKeys: Iterable<string>;
  feedFullyLoaded: boolean;
}): T[] {
  const loaded = new Set(opts.loadedGroupKeys);
  const seen = new Set<string>();
  return opts.held.filter(group => {
    if (seen.has(group.groupKey)) return false;
    seen.add(group.groupKey);
    return loaded.has(group.groupKey) || !opts.feedFullyLoaded;
  });
}

/** Most frequent hold reason, for the Auto-process status line. */
export function mostCommonHoldReason(reasons: string[]): string | undefined {
  if (!reasons.length) return undefined;
  const counts = new Map<string, number>();
  for (const reason of reasons) {
    counts.set(reason, (counts.get(reason) || 0) + 1);
  }
  let top = reasons[0];
  let topCount = 0;
  counts.forEach((count, reason) => {
    if (count > topCount) {
      top = reason;
      topCount = count;
    }
  });
  return top;
}

/**
 * Held listings stay in the unreviewed Reviews feed, so refetching offset 0
 * would see only those same cards and stop. Advance past an all-held page;
 * reset to 0 after new work so injects can shift the front of the queue.
 */
export function nextReviewsFetchOffset(opts: {
  currentOffset: number;
  fetchedCount: number;
  newWorkCount: number;
  pageSize?: number;
}): { offset: number; done: boolean } {
  const pageSize = opts.pageSize ?? REVIEWS_PAGE_SIZE;
  if (opts.fetchedCount <= 0) {
    return { offset: opts.currentOffset, done: true };
  }
  if (opts.newWorkCount <= 0) {
    if (opts.fetchedCount < pageSize) {
      return { offset: opts.currentOffset, done: true };
    }
    return { offset: opts.currentOffset + opts.fetchedCount, done: false };
  }
  return { offset: 0, done: false };
}

export function createTaskKeywordCache(
  getTask: (taskId: string) => Promise<{ keywords_text?: string } | null>
): (taskId?: string | null) => Promise<string[] | undefined> {
  const cache = new Map<string, Promise<string[] | undefined>>();
  return (taskId?: string | null) => {
    if (!taskId) return Promise.resolve(undefined);
    let pending = cache.get(taskId);
    if (!pending) {
      pending = getTask(taskId)
        .then(task => parseKeywordsFromTaskText(task?.keywords_text))
        .catch(() => undefined);
      cache.set(taskId, pending);
    }
    return pending;
  };
}

/** Run `fn` over items with a bounded worker pool. */
export async function mapPool<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T, index: number) => Promise<R>,
  shouldStop?: () => boolean
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const workerCount = Math.max(1, Math.min(concurrency, items.length || 1));

  const worker = async () => {
    while (!shouldStop?.()) {
      const index = next;
      next += 1;
      if (index >= items.length) return;
      results[index] = await fn(items[index], index);
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}
